import type { ProfilePerson } from "#shared/directoryContract.ts";
import { Card, CardContent } from "@/components/ui/card.tsx";
import { CalendarDaysIcon, ClockIcon, MapPinIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { formatLocalTime, formatUtcMonthYear } from "@packages/core/time/dateFormatting.ts";
import { Badge } from "@packages/core-client/components/Badge.tsx";
import { RoutedLink } from "@packages/core-client/components/RoutedLink.tsx";
import { SectionTitle } from "@packages/core-client/components/SectionTitle.tsx";
import { PersonAvatar } from "@packages/core-client/people/PersonAvatar.tsx";
import { PersonInlineLink } from "@packages/core-client/people/PersonInlineLink.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useTranslation } from "react-i18next";
import { i18NextInstanceKey } from "../../../i18n/i18next.ts";

export interface ProfileHeaderProps {
    person: ProfilePerson;
}

export function ProfileHeader({ person }: ProfileHeaderProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t, i18n } = useTranslation("ProfileHeader", { i18n: i18nextInstance });

    const { manager } = person;
    const showsNewHire = person.isNewHire;
    const joinedDate = formatUtcMonthYear(person.hireDate, i18n.language);
    const localTime = formatLocalTime(person.timeZone, i18n.language, new Date());

    const hasMetaRow = Boolean(manager || (person.workLocationId && person.workLocationLabel) || joinedDate || localTime);

    return (
        <Card>
            <CardContent className="@container">
                <div className="flex flex-col items-start gap-4 @md:flex-row @md:gap-6">
                    <PersonAvatar person={person} className="[--avatar-size:--spacing(16)] @md:[--avatar-size:--spacing(24)]" />
                    <div className="flex min-w-0 flex-col gap-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <SectionTitle>{person.displayName}</SectionTitle>
                            {person.pronouns && <span className="text-muted-fg text-sm">{person.pronouns}</span>}
                            {showsNewHire && (
                                <Badge intent="success">
                                    <SparklesIcon className="size-3" aria-hidden="true" />
                                    {t("newHireBadge")}
                                </Badge>
                            )}
                        </div>

                        {(person.jobTitle || (person.departmentId && person.departmentLabel)) && (
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                                {person.jobTitle && <span className="text-muted-fg">{person.jobTitle}</span>}
                                {person.jobTitle && person.departmentId && person.departmentLabel && <span className="text-muted-fg">{"·"}</span>}
                                {person.departmentId && person.departmentLabel && (
                                    <RoutedLink href={`/people/departments/${person.departmentId}`} className="text-fg font-medium">
                                        {person.departmentLabel}
                                    </RoutedLink>
                                )}
                            </div>
                        )}

                        {hasMetaRow && (
                            <div className="text-muted-fg mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                                {manager && (
                                    <span className="flex min-w-0 items-center gap-2">
                                        <span className="whitespace-nowrap">{t("reportsTo")}</span>
                                        <PersonInlineLink personId={manager.id} displayName={manager.displayName} imageUrl={manager.imageUrl} />
                                    </span>
                                )}

                                {person.workLocationId && person.workLocationLabel && (
                                    <RoutedLink href={`/people/locations/${person.workLocationId}`} className="text-muted-fg inline-flex items-center gap-1.5">
                                        <MapPinIcon className="size-4 shrink-0" aria-hidden="true" />
                                        <span>{person.workLocationLabel}</span>
                                    </RoutedLink>
                                )}

                                {joinedDate && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <CalendarDaysIcon className="size-4 shrink-0" aria-hidden="true" />
                                        <span>{t("joined", { date: joinedDate })}</span>
                                    </span>
                                )}

                                {localTime && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <ClockIcon className="size-4 shrink-0" aria-hidden="true" />
                                        <span>{localTime}</span>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
